import React, { useEffect, useState } from 'react';
import { Slider } from '@mui/material';
import { Bar, BarChart, CartesianGrid, Cell, ResponsiveContainer, XAxis, YAxis } from 'recharts';
import { Img } from 'react-image';
import { GetLevelDistributionDataAPIResponse, UIPosition } from 'types';
import _ from 'lodash';
import { generateMaxLevelForSlider, generateMinLevelForSlider } from 'src/utils/position';
import { BsArrowRight } from 'react-icons/bs';
import { useThemeContext } from 'src/context/ThemeProvider';
import { Loader } from 'ui';
import NewCreatingPositionLoader from '../NewCreatingPositionLoader';

interface Props {
	selectedJobLevel: number;
	setSelectedJobLevel: (value: number) => void;
	jobId: string;
	data?: GetLevelDistributionDataAPIResponse;
	activeIndex: number;
	setActiveIndex: React.Dispatch<React.SetStateAction<number>>;
	position?: UIPosition;
	isLoading: boolean;
}
const Histogram = ({
	selectedJobLevel,
	isLoading,
	setSelectedJobLevel,
	data,
	activeIndex,
	setActiveIndex,
	position,
}: Props) => {
	const { themeMode } = useThemeContext();
	const [chartHeight, setChartHeight] = useState<number>(0);
	const [positionLevel, setPositionLevel] = useState<number>(0);
	const [activeIndexForSuggestedLevel, setActiveIndexForSuggestedLevel] = useState<number>();

	const getActiveIndex = (value: number) => {
		const index = data?.salaryDistributionData?.findIndex(d => _.inRange(value, d.range.min, d.range.max + 1)) ?? 0;
		return index >= 0 ? index : 0;
	};

	const histogramData =
		data?.salaryDistributionData?.map(dist => ({
			name: dist.label,
			value: dist.users,
		})) ?? [];

	useEffect(() => {
		if (window.innerWidth < 960) {
			setChartHeight(150);
		} else {
			setChartHeight(250);
		}
	}, []);

	useEffect(() => {
		if (data) {
			setActiveIndexForSuggestedLevel(getActiveIndex(data.recommendedLevel || 0));
			setPositionLevel(data.recommendedLevel || 0);
			setActiveIndex(getActiveIndex(data.recommendedLevel || 0));
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [data]);

	const recommendedSalary = data?.salaryDistributionData ? data?.salaryDistributionData[activeIndex].salary : null;
	const minLevel = generateMinLevelForSlider(data?.recommendedLevel ?? 0, data);
	const maxLevel = generateMaxLevelForSlider(data?.recommendedLevel ?? 0, data);

	return (
		<div className="w-full font-inter">
			<div className="relative" style={{ minHeight: `150px` }}>
				{isLoading ? (
					// <Loader />
					<NewCreatingPositionLoader showAnimation={true} progressBar={false} customClassCss="!w-[30%] mx-auto " />
				) : (
					<ResponsiveContainer width="100%" height={chartHeight}>
						<BarChart
							width={710}
							// height={250}
							data={histogramData.map(d => ({ name: d.name, value: d.value }))}
							margin={{
								top: 0,
								right: 0,
								bottom: 0,
								left: 0,
							}}
						>
							<CartesianGrid stroke="transparent" />
							<XAxis dataKey="name" />
							<YAxis tick={false} />
							<Bar dataKey="value">
								{histogramData.map((d, i) => (
									<Cell
										key={i}
										fill={
											i === activeIndexForSuggestedLevel
												? '#06C270'
												: i === activeIndex
												  ? themeMode === 'dark'
														? '#FDDD48'
														: '#fad011'
												  : themeMode === 'dark'
												    ? '#FDED72'
												    : '#FDED72'
										}
									/>
								))}
							</Bar>
						</BarChart>
					</ResponsiveContainer>
				)}

				<div className="w-max absolute text-[#555770] dark:text-[#E4E4EB] top-[100px] sm:top-[50px] left-[-40px] sm:left-[-20px] rotate-[270deg] sm:rotate flex gap-1 items-center">
					<div className="sm:text-[11px]">No. of candidates</div>
					<BsArrowRight />
				</div>

				<div className="w-max absolute flex gap-1 text-[#555770] dark:text-[#E4E4EB] items-center bottom-1 sm:bottom-2 left-2 sm:left-4">
					<div className="sm:text-[11px]">Level</div>
					<BsArrowRight />
				</div>
			</div>
			<div className="sm:px-2">
				<Slider
					className="!p-0"
					// getAriaLabel={() => 'Level range'}
					value={positionLevel}
					onChange={(event: Event, newValue: number | number[]) => {
						setPositionLevel(newValue as number);
						setActiveIndex(getActiveIndex(Number(newValue)));
						setSelectedJobLevel(newValue as number);
					}}
					valueLabelDisplay="auto"
					max={maxLevel}
					min={minLevel}
					size={'medium'}
				/>
				<div className="text-[17px] dark:text-[#C7C9D9] flex justify-between mt-[2px]">
					<div>{minLevel}</div>
					<div>{maxLevel}</div>
				</div>
			</div>

			{recommendedSalary !== null &&
			!_.inRange(recommendedSalary, position?.salary.min ?? 0, (position?.salary.max ?? 0) + 1) ? (
				<div
					className={`dark:bg-[#1F2A37] border pt-[8px] px-[8px] pb-[8px] md:pb-[26px] flex gap-2 rounded-[10px] w-full dark:text-[#C7C9D9] mt-3 ${
						activeIndexForSuggestedLevel !== activeIndex ? 'visible' : 'invisible'
					}`}
				>
					<div className="pt-[3px] md:pt-[6px] min-w-[16px]">
						<Img src={'/assets/exclamation-circle.svg'} alt="warning" width={16} height={16} />
					</div>
					For Level {selectedJobLevel}, explore an average ₹ {Math.round(recommendedSalary)} LPA salary to attract top
					talent.
				</div>
			) : null}
		</div>
	);
};

export default Histogram;
